/**
 * DV-INF-001: Demo Viewer Error Boundary
 * Catches render-time errors and forwards them to the unified logging system
 */

import React from 'react';
import { ErrorBanner } from './ErrorNotices';
import { createDemoLogEntry } from '@/lib/demo-logger';
import type { LogEntry } from './LogsPanel';

export interface DemoErrorBoundaryProps {
  children: React.ReactNode;
  onLog?: (entry: LogEntry) => void;
  fallback?: React.ComponentType<{ error: Error; reset: () => void }>;
  className?: string;
  resetKeys?: Array<string | number | boolean>; // Reset boundary when these values change
}

interface DemoErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorId: string | null;
  resetKeys: Array<string | number | boolean> | undefined;
}

/**
 * Error boundary for Demo Viewer components
 * Catches render errors and provides structured logging
 */
export class DemoErrorBoundary extends React.Component<
  DemoErrorBoundaryProps,
  DemoErrorBoundaryState
> {
  constructor(props: DemoErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorId: null,
      resetKeys: props.resetKeys,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<DemoErrorBoundaryState> {
    // Generate unique error ID for correlation
    const errorId = `error-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;

    return {
      hasError: true,
      error,
      errorId,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('🚨 Demo Viewer Error Boundary caught error:', error, errorInfo);

    // Create structured log entry
    const logEntry = createDemoLogEntry(
      'system',
      `React error boundary caught: ${error.message}`,
      'error',
      {
        correlationId: this.state.errorId || `error-${Date.now()}`,
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        action: 'ui_error',
        component: 'DemoErrorBoundary',
      }
    );

    // Send to parent logging system
    this.props.onLog?.(logEntry);

    // Also log to console for development
    console.group('🚨 Demo Viewer Error Details');
    console.error('Error:', error);
    console.error('Component Stack:', errorInfo.componentStack);
    console.error('Error ID:', this.state.errorId);
    console.groupEnd();
  }

  componentDidUpdate(prevProps: DemoErrorBoundaryProps) {
    // Reset error boundary if resetKeys change
    const { resetKeys } = this.props;
    const prevResetKeys = prevProps.resetKeys;

    if (this.state.hasError && resetKeys && prevResetKeys) {
      const hasChangedKey = resetKeys.some((key, index) => key !== prevResetKeys[index]);
      if (hasChangedKey) {
        this.handleReset();
      }
    }

    // Update resetKeys in state
    if (resetKeys !== prevResetKeys) {
      this.setState({ resetKeys });
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorId: null,
    });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        const Fallback = this.props.fallback;
        return <Fallback error={this.state.error} reset={this.handleReset} />;
      }

      // Default error display
      const errorData = {
        type: 'server_error' as const,
        status: 500,
        message: this.state.error.message,
        userMessage: 'Demo Viewer Error',
        remediation: [
          'Try refreshing the page',
          'Check the console for more details',
          'Contact support if the problem persists'
        ],
        endpoint: 'demo-viewer',
        retryable: true,
        correlationId: this.state.errorId || undefined
      };

      return (
        <div className={this.props.className} data-testid="demo-error-boundary-fallback">
          <ErrorBanner
            error={errorData}
            onRetry={this.handleReset}
            data-testid="demo-error-banner"
          />
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook for error boundary integration
 */
export function useDemoErrorBoundary() {
  const [error, setError] = React.useState<Error | null>(null);

  // Throw error to trigger error boundary
  const throwError = React.useCallback((error: Error) => {
    setError(error);
  }, []);

  // Reset error state
  const resetError = React.useCallback(() => {
    setError(null);
  }, []);

  // Throw error on next render if set
  if (error) {
    throw error;
  }

  return {
    throwError,
    resetError,
    hasError: !!error,
  };
}

/**
 * Error boundary with logging integration
 */
export interface DemoErrorBoundaryWithLoggingProps extends DemoErrorBoundaryProps {
  source: string; // Component or page name for logging context
}

export function DemoErrorBoundaryWithLogging({
  source,
  onLog,
  children,
  ...props
}: DemoErrorBoundaryWithLoggingProps) {
  const handleLog = React.useCallback(
    (entry: LogEntry) => {
      // Add source context to the log entry
      const enhancedEntry = {
        ...entry,
        meta: {
          ...entry.meta,
          source,
          boundaryContext: true,
        },
      };
      onLog?.(enhancedEntry);
    },
    [onLog, source]
  );

  return (
    <DemoErrorBoundary onLog={handleLog} {...props}>
      {children}
    </DemoErrorBoundary>
  );
}

/**
 * HOC for adding error boundary to components
 */
export function withDemoErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  options: {
    onLog?: (entry: LogEntry) => void;
    fallback?: React.ComponentType<{ error: Error; reset: () => void }>;
    source?: string;
  } = {}
) {
  const BoundaryWrappedComponent = (props: P) => (
    <DemoErrorBoundaryWithLogging
      source={options.source || Component.displayName || Component.name}
      onLog={options.onLog}
      fallback={options.fallback}
    >
      <Component {...props} />
    </DemoErrorBoundaryWithLogging>
  );

  BoundaryWrappedComponent.displayName = `withDemoErrorBoundary(${
    Component.displayName || Component.name
  })`;

  return BoundaryWrappedComponent;
}